import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Ban,
  CalendarDays,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Loader2,
  Search,
  Users,
} from 'lucide-react'

import { AppointmentDetailModal } from '@/components/calendar/AppointmentDetailModal'
import { AppointmentOutcomeModal } from '@/components/calendar/AppointmentOutcomeModal'
import { AppointmentSummaryBar } from '@/components/calendar/AppointmentSummaryBar'
import { BlockoutManager } from '@/components/calendar/BlockoutManager'
import { BookAppointmentModal } from '@/components/calendar/BookAppointmentModal'
import { BookingSearchDrawer } from '@/components/calendar/BookingSearchDrawer'
import { ChairBoardGrid } from '@/components/calendar/ChairBoardGrid'
import { CheckInArrivalsPanel } from '@/components/calendar/CheckInArrivalsPanel'
import { DayCalendar } from '@/components/calendar/DayCalendar'
import { MonthCalendar, buildMonthDayRange } from '@/components/calendar/MonthCalendar'
import { OperationsSettingsPanel } from '@/components/calendar/OperationsSettingsPanel'
import { WalkInDrawer } from '@/components/calendar/WalkInDrawer'
import { WalkinQueuePanel } from '@/components/calendar/WalkinQueuePanel'
import { WeekCalendar } from '@/components/calendar/WeekCalendar'
import { Button } from '@/components/ui/button'
import { SalonEmptyState } from '@/components/ui/SalonEmptyState'
import { useBranch } from '@/contexts/BranchContext'
import {
  approveOnlineAppointment,
  checkInAppointment,
  fetchAppointmentSlotConfig,
  fetchAppointments,
  rescheduleAppointment,
  startAppointmentInChair,
} from '@/lib/api/appointments'
import { fetchChairs } from '@/lib/api/chairs'
import { fetchServices } from '@/lib/api/services'
import { fetchStylists } from '@/lib/api/stylists'
import { fetchWalkinQueue } from '@/lib/api/walkinQueue'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { normalizeBusinessHours } from '@/lib/businessHours'
import {
  buildThreeDayRange,
  formatDayHeading,
  formatScheduleDate,
  nextIsoDate,
  shiftIsoDate,
  todayIso,
} from '@/lib/calendar/calendarDates'
import { buildDemoAppointments, buildDemoStylists } from '@/lib/calendar/calendarDemo'
import { checkInArrivalsForDate } from '@/lib/calendar/checkIn'
import { dayKeyFromDate } from '@/lib/calendar/dayCalendar'
import {
  buildWeekDayRange,
  formatWeekHeading,
  resolveWeekGridHours,
} from '@/lib/calendar/weekCalendar'
import { SALON_DESK_APPOINTMENTS } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

/** @typedef {'day' | '3day' | 'week' | 'month'} CalendarViewMode */
/** @typedef {'floor' | 'schedule' | 'blocks'} CalendarOpsView */

const OPS_VIEWS = [
  {
    id: /** @type {CalendarOpsView} */ ('floor'),
    label: 'Floor',
    icon: LayoutGrid,
    description: 'Live chairs, check-ins, and walk-in queue.',
  },
  {
    id: /** @type {CalendarOpsView} */ ('schedule'),
    label: 'Schedule',
    icon: CalendarDays,
    description: 'Day or week grid — click a booking for details.',
  },
  {
    id: /** @type {CalendarOpsView} */ ('blocks'),
    label: 'Blocks',
    icon: Ban,
    description: 'Lunch, training, holidays, and chair/stylist blocks.',
  },
]

const deskLite = SALON_DESK_APPOINTMENTS.lite

const VISIBLE_OPS_VIEWS = deskLite
  ? OPS_VIEWS.filter((view) => view.id === 'schedule')
  : OPS_VIEWS.filter((view) => {
      if (view.id === 'floor' && !SALON_DESK_APPOINTMENTS.showFloorTab) {
        return false
      }
      if (view.id === 'blocks' && !SALON_DESK_APPOINTMENTS.showBlocksTab) {
        return false
      }
      return true
    })

/**
 * @param {import('@/types/api').Appointment[]} appointments
 * @param {string} dayDate
 * @param {number} waitingCount
 */
function buildDaySummary(appointments, dayDate, waitingCount) {
  const dayItems = appointments.filter((appointment) => appointment.starts_at.slice(0, 10) === dayDate)

  const confirmed = dayItems.filter((appointment) =>
    ['scheduled', 'confirmed'].includes(appointment.status),
  ).length
  const checkedIn = dayItems.filter((appointment) => appointment.status === 'checked_in').length
  const inService = dayItems.filter((appointment) => appointment.status === 'in_chair').length
  const completed = dayItems.filter((appointment) => appointment.status === 'completed').length
  const attention = dayItems.filter(
    (appointment) =>
      appointment.online_pending_approval ||
      appointment.status === 'no_show' ||
      (appointment.source === 'online' && appointment.online_pending_approval),
  ).length

  return {
    total: dayItems.filter((appointment) => !['cancelled', 'no_show'].includes(appointment.status))
      .length,
    confirmed,
    checkedIn,
    inService,
    waiting: waitingCount,
    completed,
    attention,
  }
}

/**
 * @param {import('@/types/api').Appointment[]} appointments
 * @param {string | null} filterKey
 * @param {string} dayDate
 */
function filterAppointmentsBySummary(appointments, filterKey, dayDate) {
  if (!filterKey || filterKey === 'total' || filterKey === 'waiting') {
    return appointments
  }

  return appointments.filter((appointment) => {
    if (appointment.starts_at.slice(0, 10) !== dayDate && filterKey !== 'attention') {
      return true
    }

    switch (filterKey) {
      case 'confirmed':
        return ['scheduled', 'confirmed'].includes(appointment.status)
      case 'checkedIn':
        return appointment.status === 'checked_in'
      case 'inService':
        return appointment.status === 'in_chair'
      case 'completed':
        return appointment.status === 'completed'
      case 'attention':
        return Boolean(appointment.online_pending_approval)
      default:
        return true
    }
  })
}

export function CalendarPage() {
  const navigate = useNavigate()
  const { activeBranchId, activeBranch } = useBranch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [opsView, setOpsView] = useState(
    /** @type {CalendarOpsView} */ (SALON_DESK_APPOINTMENTS.defaultOpsView ?? 'floor'),
  )
  const [viewMode, setViewMode] = useState(/** @type {CalendarViewMode} */ ('day'))
  const [selectedDate, setSelectedDate] = useState(todayIso)
  const [appointments, setAppointments] = useState([])
  const [stylists, setStylists] = useState([])
  const [chairs, setChairs] = useState([])
  const [slotMinutes, setSlotMinutes] = useState(15)
  const [queueEntries, setQueueEntries] = useState([])
  const [queueServices, setQueueServices] = useState([])
  const [metaLoading, setMetaLoading] = useState(true)
  const [appointmentsLoading, setAppointmentsLoading] = useState(true)
  const [queueLoading, setQueueLoading] = useState(true)
  const [reschedulingId, setReschedulingId] = useState(null)
  const [boardRefreshKey, setBoardRefreshKey] = useState(0)
  const [bookModalOpen, setBookModalOpen] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [rescheduleMode, setRescheduleMode] = useState(false)
  const [outcomeAppointment, setOutcomeAppointment] = useState(null)
  const [error, setError] = useState('')
  const [appointmentsError, setAppointmentsError] = useState('')
  const [success, setSuccess] = useState('')
  const [walkInOpen, setWalkInOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState(/** @type {string | null} */ (null))
  const [stylistFilter, setStylistFilter] = useState('')
  const [jumpTime, setJumpTime] = useState('')
  const [usingDemoData, setUsingDemoData] = useState(false)

  const businessHours = useMemo(
    () => normalizeBusinessHours(activeBranch?.business_hours),
    [activeBranch?.business_hours],
  )

  const weekRange = useMemo(() => buildWeekDayRange(selectedDate), [selectedDate])
  const threeDayRange = useMemo(() => buildThreeDayRange(selectedDate), [selectedDate])
  const monthRange = useMemo(() => buildMonthDayRange(selectedDate), [selectedDate])
  const dayHours = businessHours[dayKeyFromDate(selectedDate)] ?? { open: '10:00', close: '22:00' }
  const multiDayDates = viewMode === '3day' ? threeDayRange.dates : weekRange.dates
  const weekGridHours = useMemo(
    () => resolveWeekGridHours(businessHours, multiDayDates),
    [businessHours, multiDayDates],
  )

  const appointmentRange = useMemo(() => {
    if (viewMode === 'week') {
      return { from: weekRange.from, to: weekRange.to }
    }
    if (viewMode === '3day') {
      return { from: threeDayRange.from, to: threeDayRange.to }
    }
    if (viewMode === 'month') {
      return { from: monthRange.from, to: monthRange.to }
    }
    return { from: selectedDate, to: nextIsoDate(selectedDate) }
  }, [
    selectedDate,
    viewMode,
    weekRange.from,
    weekRange.to,
    threeDayRange.from,
    threeDayRange.to,
    monthRange.from,
    monthRange.to,
  ])

  const refreshQueue = useCallback(async () => {
    if (!activeBranchId) {
      return
    }

    setQueueLoading(true)

    try {
      const entries = await fetchWalkinQueue()
      setQueueEntries(entries)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load walk-in queue.'))
    } finally {
      setQueueLoading(false)
    }
  }, [activeBranchId])

  useEffect(() => {
    if (!activeBranchId && !SALON_DESK_APPOINTMENTS.useDemoSchedule) {
      return
    }

    let cancelled = false
    setMetaLoading(true)
    setError('')

    async function loadMeta() {
      if (SALON_DESK_APPOINTMENTS.useDemoSchedule) {
        if (!cancelled) {
          setStylists(buildDemoStylists())
          setSlotMinutes(15)
          setMetaLoading(false)
          setUsingDemoData(true)
        }
        return
      }

      const metaRequests = [fetchStylists(), fetchAppointmentSlotConfig(), fetchServices()]

      if (!deskLite) {
        metaRequests.push(fetchChairs())
      }

      const results = await Promise.allSettled(metaRequests)

      if (cancelled) {
        return
      }

      const [stylistResult, slotResult, serviceResult, chairResult] = results
      const failures = []

      if (stylistResult.status === 'fulfilled') {
        setStylists(stylistResult.value)
      } else {
        failures.push('stylists')
      }

      if (slotResult.status === 'fulfilled') {
        setSlotMinutes(slotResult.value.slot_minutes ?? 15)
      } else {
        failures.push('slot settings')
      }

      if (serviceResult.status === 'fulfilled') {
        setQueueServices(serviceResult.value.filter((service) => service.is_active))
      } else {
        failures.push('services')
      }

      if (chairResult?.status === 'fulfilled') {
        setChairs(chairResult.value)
      } else if (!deskLite) {
        failures.push('chairs')
      }

      if (failures.includes('stylists') && SALON_DESK_APPOINTMENTS.useDemoSchedule) {
        setStylists(buildDemoStylists())
        setUsingDemoData(true)
      } else if (failures.length > 0) {
        const rejected = results.find((result) => result.status === 'rejected')
        setError(
          apiErrorMessage(rejected?.reason, `Unable to load calendar settings (${failures.join(', ')}).`),
        )
      }
    }

    loadMeta().finally(() => {
      if (!cancelled) {
        setMetaLoading(false)
      }
    })

    if (SALON_DESK_APPOINTMENTS.showWalkinQueue && !SALON_DESK_APPOINTMENTS.useDemoSchedule) {
      refreshQueue()
    } else {
      setQueueLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [activeBranchId, refreshQueue])

  useEffect(() => {
    if (!activeBranchId) {
      return undefined
    }

    const shouldPoll = opsView === 'floor' || walkInOpen
    if (!shouldPoll) {
      return undefined
    }

    const timer = window.setInterval(() => {
      refreshQueue()
    }, 30000)

    return () => window.clearInterval(timer)
  }, [activeBranchId, opsView, refreshQueue, walkInOpen])

  const reloadAppointments = useCallback(async () => {
    setAppointmentsLoading(true)
    setAppointmentsError('')

    if (SALON_DESK_APPOINTMENTS.useDemoSchedule) {
      const demoStylists = stylists.length > 0 ? stylists : buildDemoStylists()
      setAppointments(buildDemoAppointments(selectedDate, demoStylists))
      setUsingDemoData(true)
      setAppointmentsLoading(false)
      return
    }

    try {
      const appointmentItems = await fetchAppointments(appointmentRange)
      setAppointments(appointmentItems)
      setUsingDemoData(false)
    } catch (err) {
      setAppointments([])
      setAppointmentsError(
        apiErrorMessage(err, 'We couldn’t load appointments from the salon server.'),
      )
    } finally {
      setAppointmentsLoading(false)
    }
  }, [appointmentRange, selectedDate, stylists])

  useEffect(() => {
    if (!activeBranchId && !SALON_DESK_APPOINTMENTS.useDemoSchedule) {
      return
    }

    let cancelled = false

    async function loadAppointments() {
      setAppointmentsLoading(true)
      setAppointmentsError('')

      if (SALON_DESK_APPOINTMENTS.useDemoSchedule) {
        const demoStylists = stylists.length > 0 ? stylists : buildDemoStylists()
        if (!cancelled) {
          setAppointments(buildDemoAppointments(selectedDate, demoStylists))
          setUsingDemoData(true)
          setAppointmentsLoading(false)
        }
        return
      }

      try {
        const appointmentItems = await fetchAppointments(appointmentRange)

        if (!cancelled) {
          setAppointments(appointmentItems)
          setUsingDemoData(false)
        }
      } catch (err) {
        if (!cancelled) {
          setAppointments([])
          setAppointmentsError(
            apiErrorMessage(err, 'We couldn’t load appointments from the salon server.'),
          )
        }
      } finally {
        if (!cancelled) {
          setAppointmentsLoading(false)
        }
      }
    }

    loadAppointments()

    return () => {
      cancelled = true
    }
  }, [activeBranchId, appointmentRange, selectedDate, stylists])

  useEffect(() => {
    const shouldBook = searchParams.get('book') === '1'
    const appointmentId = searchParams.get('appointment')

    if (shouldBook) {
      setBookModalOpen(true)
      setOpsView('schedule')
    }

    if (appointmentId && appointments.length > 0) {
      const match = appointments.find((item) => String(item.id) === appointmentId)
      if (match) {
        setSelectedAppointment(match)
        setOpsView('schedule')
      }
    }

    if (shouldBook || appointmentId) {
      const next = new URLSearchParams(searchParams)
      next.delete('book')
      next.delete('appointment')
      setSearchParams(next, { replace: true })
    }
  }, [appointments, searchParams, setSearchParams])

  useEffect(() => {
    function onKeyDown(event) {
      const target = /** @type {HTMLElement | null} */ (event.target)
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearchOpen(true)
        return
      }

      if (event.key === 'Escape') {
        setSelectedAppointment(null)
        setSearchOpen(false)
        setWalkInOpen(false)
        setBookModalOpen(false)
        return
      }

      if (typing) {
        return
      }

      const step =
        viewMode === 'week' ? 7 : viewMode === '3day' ? 3 : viewMode === 'month' ? 30 : 1

      if (event.key.toLowerCase() === 'n') {
        event.preventDefault()
        setBookModalOpen(true)
      } else if (event.key.toLowerCase() === 's') {
        event.preventDefault()
        setSearchOpen(true)
      } else if (event.key.toLowerCase() === 't') {
        event.preventDefault()
        setSelectedDate(todayIso())
        setJumpTime('')
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setSelectedDate((current) => shiftIsoDate(current, -step))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        setSelectedDate((current) => shiftIsoDate(current, step))
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [viewMode])

  /**
   * @param {number} appointmentId
   * @param {{ starts_at: string, stylist_id?: number | null }} payload
   */
  async function handleReschedule(appointmentId, payload) {
    setReschedulingId(appointmentId)
    setError('')
    setSuccess('')

    try {
      const updated = await rescheduleAppointment(appointmentId, payload)
      setAppointments((current) =>
        current.map((appointment) =>
          appointment.id === updated.id
            ? {
                ...appointment,
                ...updated,
                customer: updated.customer ?? appointment.customer,
                services: updated.services ?? appointment.services,
              }
            : appointment,
        ),
      )
      setSuccess('Appointment rescheduled.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to reschedule appointment.')
      throw err
    } finally {
      setReschedulingId(null)
    }
  }

  const waitingCount = queueEntries.filter((entry) => entry.status === 'waiting').length

  const daySummary = useMemo(
    () => buildDaySummary(appointments, selectedDate, waitingCount),
    [appointments, selectedDate, waitingCount],
  )

  const arrivalsDate = viewMode === 'day' ? selectedDate : todayIso()
  const arrivalsCount = useMemo(
    () => checkInArrivalsForDate(appointments, arrivalsDate).length,
    [appointments, arrivalsDate],
  )

  const activeOpsView = VISIBLE_OPS_VIEWS.find((view) => view.id === opsView) ?? VISIBLE_OPS_VIEWS[0]

  const heading =
    viewMode === 'week'
      ? formatWeekHeading(weekRange.dates)
      : viewMode === '3day'
        ? `${formatDayHeading(threeDayRange.dates[0])} – ${new Date(`${threeDayRange.dates[2]}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`
        : viewMode === 'month'
          ? new Date(`${selectedDate}T12:00:00`).toLocaleDateString(undefined, {
              month: 'long',
              year: 'numeric',
            })
          : formatDayHeading(selectedDate)

  const navStep =
    viewMode === 'week' ? 7 : viewMode === '3day' ? 3 : viewMode === 'month' ? monthRange.daysInMonth : 1

  /**
   * @param {string} dayDate
   */
  function openDayView(dayDate) {
    setSelectedDate(dayDate)
    setViewMode('day')
  }

  /**
   * @param {import('@/types/api').Appointment} appointment
   */
  function handleLookupAppointmentSelect(appointment) {
    setSelectedDate(appointment.starts_at.slice(0, 10))
    setViewMode('day')
    setOpsView('schedule')
    setSelectedAppointment(appointment)
  }

  /**
   * @param {{ appointments: import('@/types/api').Appointment[] }} result
   */
  async function handleBooked(result) {
    setBookModalOpen(false)
    setSuccess(
      result.appointments.length > 1
        ? `${result.appointments.length} recurring appointments booked.`
        : 'Appointment booked.',
    )

    const appointmentItems = await fetchAppointments(appointmentRange)
    setAppointments(appointmentItems)
  }

  async function handleBoardUpdated() {
    await refreshQueue()
  }

  async function handleCheckInUpdated() {
    setSuccess('Guest status updated.')
    setError('')

    try {
      const appointmentItems = await fetchAppointments(appointmentRange)
      setAppointments(appointmentItems)
      setBoardRefreshKey((current) => current + 1)
      await refreshQueue()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to refresh calendar after check-in.'))
    }
  }

  async function handleOnlineApproved() {
    try {
      const appointmentItems = await fetchAppointments(appointmentRange)
      setAppointments(appointmentItems)
      setSuccess('Online booking approved and added to the schedule.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to refresh calendar.'))
    }
  }

  async function handleOutcomeUpdated() {
    setSuccess('Appointment updated.')
    setError('')

    try {
      const appointmentItems = await fetchAppointments(appointmentRange)
      setAppointments(appointmentItems)
      setBoardRefreshKey((current) => current + 1)
      await refreshQueue()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to refresh calendar.'))
    }
  }

  const activeStylists = useMemo(
    () => stylists.filter((stylist) => stylist.is_active),
    [stylists],
  )

  const visibleStylistIds = useMemo(() => {
    if (!stylistFilter) {
      return null
    }

    return [Number(stylistFilter)]
  }, [stylistFilter])

  const calendarAppointments = useMemo(() => {
    let items = filterAppointmentsBySummary(appointments, statusFilter, selectedDate)

    if (stylistFilter) {
      const id = Number(stylistFilter)
      items = items.filter((appointment) => appointment.stylist_id === id)
    }

    return items
  }, [appointments, selectedDate, statusFilter, stylistFilter])

  const calendarReady = !metaLoading && activeStylists.length > 0
  const hasActiveFilters = Boolean(statusFilter || stylistFilter)

  function handleSummaryFilter(key) {
    if (key === 'waiting') {
      setWalkInOpen(true)
      setStatusFilter(null)
      return
    }

    setStatusFilter(key)
  }

  const viewButtons = [
    { id: /** @type {CalendarViewMode} */ ('day'), label: 'Day' },
    { id: /** @type {CalendarViewMode} */ ('3day'), label: '3 Days' },
    ...(SALON_DESK_APPOINTMENTS.showWeekView
      ? [{ id: /** @type {CalendarViewMode} */ ('week'), label: 'Week' }]
      : []),
    ...(SALON_DESK_APPOINTMENTS.showMonthView
      ? [{ id: /** @type {CalendarViewMode} */ ('month'), label: 'Month' }]
      : []),
  ]

  const scheduleUnavailable = Boolean(appointmentsError) && !usingDemoData
  const scheduleEmpty =
    !appointmentsLoading && !scheduleUnavailable && calendarAppointments.length === 0 && viewMode === 'day'

  return (
    <div
      className={cn(
        'salon-page flex h-[calc(100vh-5.5rem)] min-h-0 flex-col gap-3 overflow-hidden pb-3',
        deskLite && 'salon-appointments-lite',
      )}
    >
      <div className="flex shrink-0 flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 space-y-2.5">
          <div>
            <h1 className="flex items-center gap-2.5 text-[1.65rem] font-bold tracking-tight text-foreground sm:text-[1.85rem]">
              <CalendarDays className="h-6 w-6 text-primary" aria-hidden />
              Appointments
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Manage today’s schedule and upcoming bookings
              {usingDemoData ? (
                <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                  Demo data
                </span>
              ) : null}
            </p>
          </div>

          {opsView === 'schedule' ? (
            <AppointmentSummaryBar
              summary={daySummary}
              unavailable={scheduleUnavailable}
              activeFilter={statusFilter}
              onFilter={handleSummaryFilter}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{activeOpsView.description}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 xl:justify-end">
          {opsView === 'schedule' ? (
            <>
              <Button type="button" onClick={() => setBookModalOpen(true)} title="New appointment (N)">
                <CalendarPlus className="mr-2 h-4 w-4" />
                New appointment
              </Button>

              {SALON_DESK_APPOINTMENTS.showWalkinQueue ? (
                <Button type="button" variant="outline" onClick={() => setWalkInOpen(true)}>
                  <Users className="mr-2 h-4 w-4" />
                  Walk-ins
                  {waitingCount > 0 ? (
                    <span className="ml-1.5 rounded-full bg-foreground/10 px-1.5 py-0.5 text-[10px] font-bold tabular-nums">
                      {waitingCount}
                    </span>
                  ) : null}
                </Button>
              ) : null}

              {SALON_DESK_APPOINTMENTS.showAppointmentLookup ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSearchOpen(true)}
                  title="Search bookings (⌘K / S)"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
              ) : null}
            </>
          ) : (
            <Button type="button" variant="outline" onClick={() => setOpsView('schedule')}>
              <CalendarPlus className="mr-2 h-4 w-4" />
              Open schedule
            </Button>
          )}
        </div>
      </div>

      {VISIBLE_OPS_VIEWS.length > 1 ? (
        <div className="shrink-0">
          <div
            className="inline-flex w-full max-w-md rounded-xl border bg-card p-1 shadow-sm sm:w-auto"
            role="tablist"
            aria-label="Calendar sections"
          >
            {VISIBLE_OPS_VIEWS.map((view) => {
              const Icon = view.icon
              const isActive = opsView === view.id
              const badge =
                view.id === 'floor'
                  ? arrivalsCount + queueEntries.length
                  : view.id === 'schedule'
                    ? daySummary.total
                    : 0

              return (
                <button
                  key={view.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={cn(
                    'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors sm:flex-none sm:px-4',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                  onClick={() => setOpsView(view.id)}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {view.label}
                  {badge > 0 ? (
                    <span
                      className={cn(
                        'min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                        isActive
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : 'bg-muted text-foreground',
                      )}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {error && !appointmentsError ? (
        <div className="shrink-0 rounded-xl border border-amber-200/80 bg-amber-50/90 px-4 py-2.5 text-sm text-amber-950">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="shrink-0 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">
          {success}
        </div>
      ) : null}

      {opsView === 'floor' && SALON_DESK_APPOINTMENTS.showFloorTab ? (
        <div className="grid min-h-0 flex-1 gap-5 overflow-auto xl:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-4">
            <OperationsSettingsPanel />

            <ChairBoardGrid
              key={boardRefreshKey}
              compact
              queueEntries={queueEntries}
              onUpdated={handleBoardUpdated}
            />

            <CheckInArrivalsPanel
              appointments={appointments}
              chairs={chairs}
              selectedDate={arrivalsDate}
              onUpdated={handleCheckInUpdated}
            />
          </div>

          <WalkinQueuePanel
            stylists={activeStylists}
            entries={queueEntries}
            onEntriesChange={setQueueEntries}
            services={queueServices}
            queueLoading={queueLoading || metaLoading}
            onRefreshQueue={refreshQueue}
          />
        </div>
      ) : null}

      {opsView === 'schedule' ? (
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border/50 bg-card/40">
          <div className="flex shrink-0 flex-col gap-2.5 border-b border-border/40 px-3 py-2.5 sm:px-4">
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex h-9 items-center gap-2 rounded-xl border border-border/60 bg-card px-3 text-[12px] shadow-sm">
                <span className="font-medium text-muted-foreground">Stylist</span>
                <select
                  value={stylistFilter}
                  onChange={(event) => setStylistFilter(event.target.value)}
                  className="min-w-[10rem] bg-transparent font-semibold text-foreground outline-none"
                  aria-label="Filter by stylist"
                >
                  <option value="">All stylists</option>
                  {activeStylists.map((stylist) => (
                    <option key={stylist.id} value={stylist.id}>
                      {stylist.name}
                    </option>
                  ))}
                </select>
              </label>

              {hasActiveFilters ? (
                <button
                  type="button"
                  className="text-[12px] font-medium text-primary hover:underline"
                  onClick={() => {
                    setStatusFilter(null)
                    setStylistFilter('')
                  }}
                >
                  Clear filters
                </button>
              ) : null}

              {appointmentsLoading ? (
                <span className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Updating…
                </span>
              ) : (
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {dayHours.open} – {dayHours.close}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setSelectedDate((current) => shiftIsoDate(current, -navStep))}
                  aria-label="Previous"
                  title="Previous (←)"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => {
                    setSelectedDate(todayIso())
                    setJumpTime('')
                  }}
                  title="Today (T)"
                >
                  Today
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setSelectedDate((current) => shiftIsoDate(current, navStep))}
                  aria-label="Next"
                  title="Next (→)"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="inline-flex items-center gap-1.5 rounded-xl border border-border/60 bg-card px-2 py-1 shadow-sm">
                <label className="flex items-center gap-1.5">
                  <span className="sr-only">Date</span>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                    className="h-7 bg-transparent text-[13px] font-semibold text-foreground outline-none"
                    aria-label="Go to date"
                  />
                </label>
                <span className="text-border" aria-hidden>
                  |
                </span>
                <label className="flex items-center gap-1.5">
                  <span className="sr-only">Time</span>
                  <input
                    type="time"
                    value={jumpTime}
                    onChange={(event) => setJumpTime(event.target.value)}
                    className="h-7 bg-transparent text-[13px] font-semibold text-foreground outline-none"
                    aria-label="Go to time"
                    step={slotMinutes * 60}
                  />
                </label>
              </div>

              <p className="min-w-0 flex-1 truncate text-[15px] font-semibold tracking-tight text-foreground calendar-day-heading">
                {viewMode === 'day' ? formatScheduleDate(selectedDate) : heading}
                {jumpTime ? (
                  <span className="ml-2 text-[13px] font-medium text-muted-foreground">
                    · {jumpTime}
                  </span>
                ) : null}
              </p>

              <div
                className="inline-flex rounded-full bg-muted/50 p-0.5"
                role="group"
                aria-label="Calendar view"
              >
                {viewButtons.map((view) => (
                  <button
                    key={view.id}
                    type="button"
                    className={cn(
                      'rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
                      viewMode === view.id
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    onClick={() => setViewMode(view.id)}
                  >
                    {view.label}
                  </button>
                ))}
              </div>
            </div>

            {appointmentsError && !usingDemoData ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-[13px] text-amber-950">
                <div>
                  <p className="font-semibold">Schedule unavailable</p>
                  <p className="text-[12px] text-amber-900/80">{appointmentsError}</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => reloadAppointments()}>
                  Try again
                </Button>
              </div>
            ) : null}
          </div>

          <div className="relative flex min-h-0 flex-1 flex-col">
            {metaLoading ? (
              <div className="space-y-3 p-4" aria-busy="true" aria-label="Loading schedule">
                <div className="flex gap-3">
                  <div className="h-14 w-14 animate-pulse rounded-lg bg-muted" />
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="h-14 flex-1 animate-pulse rounded-xl bg-muted" />
                  ))}
                </div>
                {Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className="h-9 animate-pulse rounded-md bg-muted/60" />
                ))}
              </div>
            ) : !calendarReady ? (
              <SalonEmptyState
                message={
                  stylists.length > 0
                    ? 'No active staff on the schedule. Turn someone on in Staff.'
                    : 'Add at least one staff member to see the calendar.'
                }
                actionLabel={deskLite ? 'Go to Staff' : undefined}
                onAction={deskLite ? () => navigate('/staff') : undefined}
              />
            ) : viewMode === 'week' && SALON_DESK_APPOINTMENTS.showWeekView ? (
              <div className="min-h-0 flex-1 overflow-auto">
                <WeekCalendar
                  weekDates={weekRange.dates}
                  appointments={calendarAppointments}
                  stylists={activeStylists}
                  businessHours={businessHours}
                  openTime={weekGridHours.openTime}
                  closeTime={weekGridHours.closeTime}
                  slotMinutes={slotMinutes}
                  today={todayIso()}
                  onReschedule={handleReschedule}
                  onSelectAppointment={setSelectedAppointment}
                  rescheduleEnabled={rescheduleMode}
                  onSelectDay={openDayView}
                  reschedulingId={reschedulingId}
                />
              </div>
            ) : viewMode === '3day' ? (
              <div className="min-h-0 flex-1 overflow-auto">
                <WeekCalendar
                  weekDates={threeDayRange.dates}
                  appointments={calendarAppointments}
                  stylists={activeStylists}
                  businessHours={businessHours}
                  openTime={weekGridHours.openTime}
                  closeTime={weekGridHours.closeTime}
                  slotMinutes={slotMinutes}
                  today={todayIso()}
                  onReschedule={handleReschedule}
                  onSelectAppointment={setSelectedAppointment}
                  rescheduleEnabled={rescheduleMode}
                  onSelectDay={openDayView}
                  reschedulingId={reschedulingId}
                />
              </div>
            ) : viewMode === 'month' && SALON_DESK_APPOINTMENTS.showMonthView ? (
              <div className="min-h-0 flex-1 overflow-auto">
                <MonthCalendar
                  anchorDate={selectedDate}
                  appointments={calendarAppointments}
                  today={todayIso()}
                  onSelectDay={openDayView}
                />
              </div>
            ) : (
              <DayCalendar
                date={selectedDate}
                appointments={calendarAppointments}
                stylists={activeStylists}
                openTime={dayHours.open}
                closeTime={dayHours.close}
                slotMinutes={slotMinutes}
                onReschedule={handleReschedule}
                onSelectAppointment={setSelectedAppointment}
                onCreateAppointment={() => setBookModalOpen(true)}
                scheduleUnavailable={scheduleUnavailable}
                scheduleEmpty={scheduleEmpty}
                rescheduleEnabled={rescheduleMode}
                reschedulingId={reschedulingId}
                visibleStylistIds={visibleStylistIds}
                scrollToTime={jumpTime || null}
              />
            )}
          </div>
        </section>
      ) : null}

      {opsView === 'blocks' && SALON_DESK_APPOINTMENTS.showBlocksTab ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setSelectedDate((current) => shiftIsoDate(current, -1))}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" onClick={() => setSelectedDate(todayIso())}>
              Today
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setSelectedDate((current) => shiftIsoDate(current, 1))}
              aria-label="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="rounded-xl border bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-sm">
              {formatDayHeading(selectedDate)}
            </div>
          </div>

          <BlockoutManager
            range={
              viewMode === 'week'
                ? { from: weekRange.from, to: weekRange.to }
                : viewMode === 'month'
                  ? { from: monthRange.from, to: monthRange.to }
                  : { from: selectedDate, to: nextIsoDate(selectedDate) }
            }
            stylists={activeStylists}
            chairs={chairs}
            defaultDate={selectedDate}
          />
        </div>
      ) : null}

      <BookAppointmentModal
        open={bookModalOpen}
        onClose={() => setBookModalOpen(false)}
        selectedDate={selectedDate}
        openTime={viewMode === 'day' ? dayHours.open : weekGridHours.openTime}
        closeTime={viewMode === 'day' ? dayHours.close : weekGridHours.closeTime}
        slotMinutes={slotMinutes}
        stylists={activeStylists}
        onBooked={handleBooked}
      />

      <AppointmentDetailModal
        open={selectedAppointment !== null}
        appointment={selectedAppointment}
        stylists={activeStylists}
        openTime={viewMode === 'day' ? dayHours.open : weekGridHours.openTime}
        closeTime={viewMode === 'day' ? dayHours.close : weekGridHours.closeTime}
        slotMinutes={slotMinutes}
        onClose={() => setSelectedAppointment(null)}
        onReschedule={handleReschedule}
        onApproveOnline={async (appointmentId) => {
          await approveOnlineAppointment(appointmentId)
          await handleOnlineApproved()
          setSelectedAppointment(null)
        }}
        onOpenOutcome={setOutcomeAppointment}
        onCheckIn={async (appointment) => {
          const updated = await checkInAppointment(appointment.id)
          setAppointments((current) =>
            current.map((item) =>
              item.id === appointment.id ? { ...item, ...updated, status: updated.status ?? 'checked_in' } : item,
            ),
          )
          setSelectedAppointment((current) =>
            current && current.id === appointment.id
              ? { ...current, ...updated, status: updated.status ?? 'checked_in' }
              : current,
          )
          setSuccess('Guest checked in.')
        }}
        onStartService={async (appointment) => {
          const updated = await startAppointmentInChair(appointment.id)
          setAppointments((current) =>
            current.map((item) => (item.id === appointment.id ? { ...item, ...updated } : item)),
          )
          setSelectedAppointment((current) =>
            current && current.id === appointment.id ? { ...current, ...updated } : current,
          )
          setSuccess('Service started.')
        }}
        rescheduling={selectedAppointment !== null && reschedulingId === selectedAppointment.id}
      />

      <AppointmentOutcomeModal
        open={outcomeAppointment !== null}
        appointment={outcomeAppointment}
        onClose={() => setOutcomeAppointment(null)}
        onUpdated={handleOutcomeUpdated}
      />

      {SALON_DESK_APPOINTMENTS.showWalkinQueue ? (
        <WalkInDrawer
          open={walkInOpen}
          onClose={() => setWalkInOpen(false)}
          stylists={activeStylists}
          entries={queueEntries}
          onEntriesChange={setQueueEntries}
          services={queueServices}
          queueLoading={queueLoading || metaLoading}
          onRefreshQueue={refreshQueue}
        />
      ) : null}

      {SALON_DESK_APPOINTMENTS.showAppointmentLookup ? (
        <BookingSearchDrawer
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelectAppointment={handleLookupAppointmentSelect}
        />
      ) : null}
    </div>
  )
}
